import SafeImage from '@ui/SafeImage';
import { slugify } from '@util/urlHelpers';
import type { CategoryDocument } from 'models/category';
import Link from 'next/link';
import sanitize from 'sanitize-html';
import {
  getVehicleLabel,
  type VehicleForSaleRideType,
} from '../utils';

const FEATURED_MAKES = [
  'Beta',
  'Cobra',
  'Gas Gas',
  'Honda',
  'Husqvarna',
  'KTM',
  'Kawasaki',
  'Suzuki',
  'Yamaha',
];

interface ManufacturerInfo {
  image?: string;
  name?: string;
  description?: string;
  second_description?: string;
}

interface VehicleForSaleSeoSectionsProps {
  rideType: VehicleForSaleRideType;
  forSaleSlug: string;
  manufacturerInfo?: ManufacturerInfo;
  dirtBikeCats: CategoryDocument[];
  ridingGearCats: CategoryDocument[];
}

// The SEO content rendered below the product grid: the manufacturer-info
// block, the "Shop by Manufacturer" link list, and (dirt bikes only) the
// Parts / Riding Gear link blocks.
const VehicleForSaleSeoSections = ({
  rideType,
  forSaleSlug,
  manufacturerInfo,
  dirtBikeCats,
  ridingGearCats,
}: VehicleForSaleSeoSectionsProps) => {
  const label = getVehicleLabel(rideType);
  const isDirtBikes = rideType === 'Dirt Bikes';

  return (
    <>
      {!!manufacturerInfo && (
        <div className="hidden w-full gap-[1.2rem]  bg-white px-[1.6rem] sm:block sm:pl-default-padding-x">
          <div className="mx-auto flex max-w-default gap-x-[1.5rem] px-5 py-7">
            <SafeImage
              className="max-h-[64px] object-contain"
              src={manufacturerInfo.image ?? ''}
              alt={manufacturerInfo.name ?? ''}
              key={manufacturerInfo.image}
              width={64}
              height={64}
            />
            <div>
              <h2 className="mb-2 text-left text-2xl font-semibold">
                {manufacturerInfo.name}
              </h2>
              {manufacturerInfo.description && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitize(manufacturerInfo.description),
                  }}
                ></div>
              )}
              {manufacturerInfo.second_description && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitize(manufacturerInfo.second_description),
                  }}
                ></div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="w-full bg-[#F4F4F4] p-8">
        <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0 ">
          <span className="text-[1.8rem] font-semibold">
            Shop {label} By Manufacturer
          </span>
          <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
            {FEATURED_MAKES.map((make) => (
              <Link
                className="underline"
                key={make}
                href={`/${slugify(make)}-${forSaleSlug}`}
              >
                {make} {label} For Sale
              </Link>
            ))}
          </div>
        </div>
      </div>
      {isDirtBikes && !!dirtBikeCats.length && (
        <div className="w-full bg-[#F4F4F4] p-8">
          <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0">
            <Link
              href={`/shop/dirt-bike-parts`}
              className="text-[1.8rem] font-semibold"
            >
              Shop Dirt Bike Parts
            </Link>
            <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
              {dirtBikeCats.map((category) => (
                <Link
                  className="underline"
                  key={category.full_slug}
                  href={category.full_slug}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
      {isDirtBikes && !!ridingGearCats.length && (
        <div className="w-full bg-[#F4F4F4] p-8">
          <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0">
            <Link
              href={'/shop/dirt-bike-riding-gear'}
              className="text-[1.8rem] font-semibold"
            >
              Shop Dirt Bike Riding Gear
            </Link>
            <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
              {ridingGearCats.map((category) => (
                <Link
                  className="underline"
                  key={category.full_slug}
                  href={category.full_slug}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleForSaleSeoSections;
